import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { LanguageSwitch } from '../components/LanguageSwitch';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Shield, Loader2, AlertCircle } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminLogin = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const response = await axios.post(`${API}/admin/login`, { email, password });
      
      if (response.data.success) {
        // Store token and role info
        localStorage.setItem('apollo_admin_token', response.data.token);
        localStorage.setItem('apollo_admin_role', response.data.role);
        localStorage.setItem('apollo_admin_groups', JSON.stringify(response.data.groups));
        localStorage.setItem('apollo_admin_name', response.data.name);
        navigate('/admin-secure/dashboard');
      }
    } catch (err) {
      console.error('Login error:', err);
      setError(t('admin.login.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 px-6 py-4 flex justify-between items-center">
        <div className="flex items-center gap-3">
          <img src="/apollo-logo.png" alt="Apollo" className="h-10 w-auto" />
        </div>
        <LanguageSwitch />
      </header>

      {/* Main Content */}
      <main className="flex items-center justify-center min-h-screen px-4">
        <Card className="bg-gray-800/90 border-gray-700 max-w-md w-full backdrop-blur-sm shadow-xl" data-testid="admin-login-card">
          <CardContent className="pt-10 pb-10 px-8">
            {/* Header */}
            <div className="text-center mb-8">
              <div className="w-16 h-16 rounded-full bg-orange-500/20 flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-orange-500" />
              </div>
              <h1 className="text-2xl font-manrope font-bold text-white mb-2">
                {t('admin.login.title')}
              </h1>
              <p className="text-gray-400 font-dm-sans text-sm">
                {t('admin.login.subtitle')}
              </p>
            </div>

            {/* Error Message */}
            {error && (
              <div className="mb-6 p-4 rounded-lg bg-red-500/10 border border-red-500/30 flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                <p className="text-red-400 text-sm font-dm-sans">{error}</p>
              </div>
            )}

            {/* Login Form */}
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-gray-300 font-dm-sans">
                  {t('admin.login.email')}
                </Label>
                <Input
                  id="email"
                  data-testid="admin-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="bg-gray-900/50 border-gray-700 focus:border-orange-500 focus:ring-orange-500/20 text-white placeholder:text-gray-500"
                  placeholder="admin@apollo.com"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-gray-300 font-dm-sans">
                  {t('admin.login.password')}
                </Label>
                <Input
                  id="password"
                  data-testid="admin-password-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="bg-gray-900/50 border-gray-700 focus:border-orange-500 focus:ring-orange-500/20 text-white placeholder:text-gray-500"
                  placeholder="********"
                  required
                />
              </div>

              <Button
                data-testid="admin-login-btn"
                type="submit"
                disabled={loading}
                className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold py-6 rounded-md shadow-[0_0_15px_rgba(249,115,22,0.3)] transition-all hover:scale-[1.02]"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ...
                  </>
                ) : (
                  t('admin.login.submit')
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};
